import { useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import CookieConsent from "react-cookie-consent";
import {
  COOKIE_NAME,
  COOKIE_PREFERENCES_CHANGE_EVENT,
  type CookiePreferences,
  DECLINE_VALUE,
  buildPreferences,
  emitCookiePreferencesChange,
  getStoredCookiePreferences,
  parsePreferences,
  serializePreferences,
} from "~/lib/cookiePreferences";

type PreferenceToggleProps = {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
};

const PreferenceToggle = ({ label, description, enabled, onChange }: PreferenceToggleProps) => {
  const labelId = useId();
  const descriptionId = useId();

  return (
    <div className="flex items-start justify-between gap-6 rounded-xl bg-white/5 px-5 py-4 sm:px-6 sm:py-5">
      <div className="max-w-[75%] text-sm sm:max-w-none sm:text-base">
        <p id={labelId} className="font-semibold text-white">
          {label}
        </p>
        <p id={descriptionId} className="mt-1 text-white/80">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 sm:h-8 sm:w-14 ${
          enabled ? "bg-primary-400" : "bg-white/30"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition sm:h-6 sm:w-6 ${
            enabled ? "translate-x-6 sm:translate-x-7" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
};

const HiddenButton = () => null;

type CookieConsentInstance = InstanceType<typeof CookieConsent>;
const DEFAULT_PREFERENCES = buildPreferences(false, false);
const DEFAULT_PREFERENCES_VALUE = serializePreferences(DEFAULT_PREFERENCES);

const subscribeToCookiePreferences = (callback: () => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(COOKIE_PREFERENCES_CHANGE_EVENT, callback);

  return () =>
    window.removeEventListener(COOKIE_PREFERENCES_CHANGE_EVENT, callback);
};

const getCookiePreferencesSnapshot = () =>
  serializePreferences(getStoredCookiePreferences() ?? DEFAULT_PREFERENCES);

const CookieBanner = () => {
  const consentRef = useRef<CookieConsentInstance | null>(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [draftPreferences, setDraftPreferences] =
    useState<CookiePreferences | null>(null);

  const storedPreferencesValue = useSyncExternalStore(
    subscribeToCookiePreferences,
    getCookiePreferencesSnapshot,
    () => DEFAULT_PREFERENCES_VALUE
  );
  const storedPreferences = useMemo(
    () => parsePreferences(storedPreferencesValue) ?? DEFAULT_PREFERENCES,
    [storedPreferencesValue]
  );

  const effectivePreferences = draftPreferences ?? storedPreferences;
  const analyticsAllowed = effectivePreferences.analytics;
  const marketingAllowed = effectivePreferences.marketing;

  const updateDraftPreferences = (updates: Partial<CookiePreferences>) => {
    setDraftPreferences((currentDraft) =>
      buildPreferences(
        updates.analytics ??
          currentDraft?.analytics ??
          storedPreferences.analytics,
        updates.marketing ??
          currentDraft?.marketing ??
          storedPreferences.marketing
      )
    );
  };

  const cookieValue = useMemo(
    () => serializePreferences(effectivePreferences),
    [effectivePreferences]
  );

  const scheduleAction = (action: () => void) => {
    if (typeof window === "undefined") {
      return;
    }
    window.setTimeout(() => {
      action();
      emitCookiePreferencesChange();
    }, 0);
  };

  const handleSavePreferences = () => {
    scheduleAction(() => {
      consentRef.current?.accept();
    });
  };

  const handleAcceptAll = () => {
    setDraftPreferences(buildPreferences(true, true));
    scheduleAction(() => {
      consentRef.current?.accept();
    });
  };

  const handleNecessaryOnly = () => {
    setDraftPreferences(buildPreferences(false, false));
    scheduleAction(() => {
      consentRef.current?.decline();
    });
  };

  return (
    <CookieConsent
      ref={consentRef}
      cookieName={COOKIE_NAME}
      location="bottom"
      buttonText="Zapisz preferencje"
      declineButtonText="Tylko niezbędne"
      disableStyles
      enableDeclineButton={false}
      ButtonComponent={HiddenButton}
      buttonWrapperClasses="hidden"
      cookieValue={cookieValue}
      declineCookieValue={DECLINE_VALUE}
      setDeclineCookie
      containerClasses="print-hidden fixed inset-x-0 bottom-0 z-[100] flex justify-center px-4 pb-4 sm:px-6 sm:pb-6"
      contentClasses="w-full max-w-4xl overflow-hidden rounded-3xl bg-primary-900/95 text-white shadow-2xl backdrop-blur-md"
      ariaAcceptLabel="Zapisz preferencje dotyczące plików cookie"
      ariaDeclineLabel="Ustaw tylko niezbędne pliki cookie"
      expires={180}
      sameSite="Lax"
      extraCookieOptions={{ path: "/" }}
      acceptOnScroll={false}
      acceptOnOverlayClick={false}
    >
      <div className="flex flex-col gap-4 p-4 text-sm leading-relaxed sm:gap-6 sm:p-8 sm:text-base">
        <div className="space-y-2 sm:space-y-3">
          <p className="text-lg font-semibold sm:text-2xl">
            Szanujemy Twoją prywatność
          </p>
          <p className="text-white/90">
            Korzystamy z plików cookie, aby zapewnić prawidłowe działanie strony,
            analizować ruch oraz dopasowywać treści marketingowe. Zawsze aktywny
            pozostaje pakiet niezbędny do działania serwisu. Szczegóły znajdziesz w
            naszej
            <Link
              href="/polityka-prywatnosci"
              className="ml-1 inline-flex items-center font-semibold underline decoration-primary-300 decoration-2 underline-offset-4 transition hover:text-primary-200"
            >
              Polityce prywatności
            </Link>
            .
          </p>
        </div>

        {showPreferences && (
          <div className="space-y-2 sm:space-y-3">
            <PreferenceToggle
              label="Analityczne"
              description="Pomagają nam monitorować statystyki i ulepszać serwis."
              enabled={analyticsAllowed}
              onChange={(value) => updateDraftPreferences({ analytics: value })}
            />
            <PreferenceToggle
              label="Marketingowe"
              description="Pozwalają dopasowywać komunikaty marketingowe do Twoich potrzeb."
              enabled={marketingAllowed}
              onChange={(value) => updateDraftPreferences({ marketing: value })}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/10 pt-3 sm:gap-3 sm:pt-5">
          <button
            type="button"
            onClick={handleNecessaryOnly}
            className="inline-flex items-center justify-center rounded-full border border-white/40 px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 sm:px-5 sm:py-3 sm:text-base"
          >
            Tylko niezbędne
          </button>
          {showPreferences ? (
            <button
              type="button"
              onClick={handleSavePreferences}
              className="inline-flex items-center justify-center rounded-full bg-white/15 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 sm:px-5 sm:py-3 sm:text-base"
            >
              Zapisz mój wybór
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowPreferences(true)}
              className="inline-flex items-center justify-center rounded-full bg-white/15 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 sm:px-5 sm:py-3 sm:text-base"
            >
              Zarządzaj
            </button>
          )}
          <button
            type="button"
            onClick={handleAcceptAll}
            className="inline-flex items-center justify-center rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-primary-900 transition hover:bg-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 sm:px-5 sm:py-3 sm:text-base"
          >
            Akceptuj wszystkie
          </button>
        </div>
      </div>
    </CookieConsent>
  );
};

export default CookieBanner;
